import { useCallback, useState } from "react";
import { processError } from "../../../../common/utils/base_error";
import { ControleServicosForm } from "../schemas/ControleServicos_lista/ControleServicos";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { GetAllControleServicosDao } from "../../data/daos/read/get_all_controle_servicos";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useFilters } from "../hooks/ControleMateriais_lista/filters";
import { useControleServicosListaPagination } from "../hooks/ControleServicos_lista/paginagion";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";

type LoadFilterFuncState = {
  nroMaterial?: string;
  descricao?: string;
  ativo?: string;
  CurrentPage?: number | null;
  RowsPerPage?: number | null;
};

export const useControleServicosListaViewModel = () => {
  const {
    reports: {
      getReportObraServicosControladosUseCase,
    },
    obras: {
      getAllControleServicosUseCase,
      createControleServicosUseCase,
      updateControleServicosUseCase,
      getControleServicosByIdUseCase,
      deleteControleServicosUseCase,
    },
  } = useDependencyInjection();

  const filters = useFilters();
  const pagination = useControleServicosListaPagination();
  const [controleServicos, setControleServicos] = useState<GetAllControleServicosDao[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [loading, setLoading] = useState(false);
  const { pushErrorNotification } = usePageLoader();
  const debouncedLoad = useCreateDebounceHandler(350);
  const [abrirModal, setAbrirModal] = useState(false);
  const [editmod, setEditmod] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dadosEdit, setDadosEdit] = useState<ControleServicosForm | null>(null);
  const [idControleServicos, setIdControleServicos] = useState<number>();
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  //#region Loads
  usePageStartBootstrap(async () => {
    try {
      await load();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados das páginas!")[0]);
    }
  });
  //#endregion


  //#region GetAll / Tabela
  const load = useCallback(
    async (funcState?: LoadFilterFuncState) => {
      try {
        const filterpage = funcState?.CurrentPage ?? pagination.page;
        const filterRowspage = funcState?.RowsPerPage ?? pagination.rowsPerPage;
        const filternroServico = funcState?.nroMaterial ?? filters.numero ?? "";
        const filterdescricao = funcState?.descricao ?? filters.descricao ?? "";
        const filterativo = funcState?.ativo ?? filters.status ?? "T";

        await getAllControleServicosUseCase.getAllControleServicos({
          nroServico: filternroServico,
          descricao: filterdescricao,
          ativo: filterativo,
          page: filterpage,
          rowsPerPage: filterRowspage,
        });
        setControleServicos(getAllControleServicosUseCase.items);
        pagination.setRowsPerPage(getAllControleServicosUseCase.pageSize);
        pagination.setTotalItems(getAllControleServicosUseCase.totalItems);
      } catch (e: unknown) {
        throw new Error("Falha ao buscar serviços!");
      }
    },
    [getAllControleServicosUseCase, pagination, filters],
  );

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await load();
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, "Falha ao buscar dados das páginas!")[0]);
    } finally {
      setLoading(false);
    }
  }, [
    load,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useControleServicosListaPagination.setState({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
      } finally {
        setLoading(false);
      }
    },
    [pushErrorNotification, load],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useControleServicosListaPagination.setState({
          page: 1,
          rowsPerPage,
        });
        await load({ CurrentPage: 1, RowsPerPage: rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
      } finally {
        setLoading(false);
      }
    },
    [pushErrorNotification, load],
  );
  //#endregion

  //#region Filtro
  const handleDescricaoChange = useDebouncedCallback(
    debouncedLoad,
    (descricao: string | null) => {
      filters.setDescricao(descricao);
      return async () => {
        try {
          await load({ descricao: descricao ?? undefined });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
        }
      };
    },
    [filters, load],
  );

  const handleNumeroChange = useDebouncedCallback(
    debouncedLoad,
    (numero: string | null) => {
      filters.setNumero(numero);
      return async () => {
        try {
          await load({ nroMaterial: numero ?? undefined });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da página!"));
        }
      };
    },
    [filters, load],
  );

  const handleStatusChange = useCallback(
    (novoStatus: string | null) => {
      filters.setStatus(novoStatus ?? "T");
      load({ ativo: novoStatus ?? "T" });
    },
    [filters, load],
  );

  const handleClear = useCallback(async () => {
    filters.clear();
    try {
      await load({
        ativo: "T",
        descricao: undefined,
        nroMaterial: undefined,
        CurrentPage: undefined,
        RowsPerPage: undefined,
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao atualizar página!"));
    }
  }, [filters, load]);
  //#endregion

  function converteArquivoParaBase64(file?: File | null): Promise<string | undefined> {
    if (!file) {
      return Promise.resolve(undefined);
    }
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          const base64Pure = reader.result.split(",")[1];

          resolve(base64Pure);
        } else {
          reject(new Error("Erro ao ler o arquivo: resultado não é string."));
        }
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  }

  const handleFuncionarioAssinaturaDownload = useCallback(
    async (idServicosControlados: number) => {
      try {
        const response = await getControleServicosByIdUseCase.getControleServicosById(idServicosControlados);
        if (response.arquivo && response.nomeArquivo) {
          const link = document.createElement("a");
          link.href = `data:application/octet-stream;base64,${response.arquivo}`;
          link.download = response.nomeArquivo;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao fazer download do arquivo!"));
      }
    },
    [getControleServicosByIdUseCase, pushErrorNotification],
  );

  //#region Create/Update
  const abrirModalCreate = useCallback(() => {
    setDadosEdit(null);
    setAbrirModal(true);
    setEditmod(false);
  }, [setAbrirModal, setEditmod, abrirModal, dadosEdit]);

  const fecharmodal = useCallback(() => {
    setAbrirModal(false);
    setDadosEdit(null);
  }, [setDadosEdit, dadosEdit]);

  const handleDocumentoSubmit = useCallback(
    async (form: ControleServicosForm) => {
      try {
        setSaving(true);
        if (editmod == false) {
          await createControleServicosUseCase.createControleServicos({
            nroServico: form.nroServico,
            descricao: form.descricao,
            ativo: form.ativo,
            nomeArquivo: form.nomeArquivo,
            arquivo: await converteArquivoParaBase64(form.arquivo),
          });
          await load();
        } else {
          await updateControleServicosUseCase.updateControleServicos({
            idServicosControlados: form.idServicosControlados,
            nroServico: form.nroServico,
            descricao: form.descricao,
            ativo: form.ativo,
            nomeArquivo: form.nomeArquivo,
            arquivo: await converteArquivoParaBase64(form.arquivo),
          });
          await load();
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao salvar serviço!"));
      } finally {
        setSaving(false);
        setAbrirModal(false);
      }
    },
    [createControleServicosUseCase, updateControleServicosUseCase, editmod],
  );
  //#endregion

  //#region GetById

  function base64ToFile(base64: string, filename: string): File {
    const arr = base64.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
    const bstr = atob(arr.length > 1 ? arr[1] : arr[0]);
    const n = bstr.length;
    const u8arr = new Uint8Array(n);

    for (let i = 0; i < n; i++) {
      u8arr[i] = bstr.charCodeAt(i);
    }

    return new File([u8arr], filename, { type: mime });
  }

  const getbyid = useCallback(
    async (id: number | null) => {
      setLoading(true);
      try {
        if (id != null) {
          const response = await getControleServicosByIdUseCase.getControleServicosById(id);

          const file =
            response.arquivo && typeof response.arquivo === "string"
              ? base64ToFile(response.arquivo, response.nomeArquivo || "arquivo")
              : null;

          await setDadosEdit({
            ...response,
            arquivo: file, // agora o form tem o File
          } as ControleServicosForm);
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar documento!"));
      } finally {
        setLoading(false);
      }
    },
    [getControleServicosByIdUseCase, setDadosEdit],
  );

  const handleEditControleServicosOpenState = useCallback(
    (idControleServicos: number | null, openState: boolean) => {
      if (openState) {
        getbyid(idControleServicos);
        setAbrirModal(true);
        setEditmod(true);
      }
    },
    [getbyid, editmod],
  );
  //#endregion

  //#region Delete
  const DeleteOpenModal = useCallback(
    (id: number | null, openState: boolean) => {
      if (openState) {
        if (id != null) {
          setIdControleServicos(id);
        } else {
          setIdControleServicos(undefined);
        }
      }
      setOpenDeleteModal(openState);
    },
    [idControleServicos, setIdControleServicos],
  );

  const DeletarControleServicos = useCallback(async () => {
    setLoading(true);
    if (idControleServicos === undefined) return;
    try {
      await deleteControleServicosUseCase.deleteControleServicos(idControleServicos);
      await load();
    } catch {
    } finally {
      setLoading(false);
      setOpenDeleteModal(false);
    }
  }, [idControleServicos, deleteControleServicosUseCase]);
  //#endregion

  const handleOpenReportViewer = useCallback(async () => {
    try {
      setLoading(true);

      await startReportViewerProcess({
        fetchCallback: async (requiredMimeType) =>
          await getReportObraServicosControladosUseCase.getReportObraServicosControlados(requiredMimeType),
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao gerar relatório!"));
    } finally {
      setLoading(false);
    }
  }, [
    getReportObraServicosControladosUseCase,
    startReportViewerProcess,
    pushErrorNotification,
  ]);

  return {
    filters,
    pagination,
    loading,
    errorMessage,
    controleServicos: controleServicos ?? [],
    abrirModal,
    saving,
    dadosEdit,
    openDeleteModal,
    editmod,
    reportViewerState,
    handleReload,
    handlePageChange,
    handleRowsPerPageChange,
    handleDescricaoChange,
    handleNumeroChange,
    handleStatusChange,
    handleClear,
    fecharmodal,
    abrirModalCreate,
    handleDocumentoSubmit,
    handleEditControleServicosOpenState,
    DeleteOpenModal,
    DeletarControleServicos,
    handleFuncionarioAssinaturaDownload,
    handleOpenReportViewer,
  };
};
